import {
  PagedResponse,
  PatchWithMeta,
  ValidatedResponse,
} from "@ofqual/portal-core/core/types";
import {
  all,
  call,
  getContext,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { SvrSubmission } from "../services/domain/SvrSubmission";
import {
  ActionPayload,
  CREATED_SUBMISSION,
  CREATING_SUBMISSION,
  CREATING_SUBMISSION_VALIDATION_FAIL,
  DEACTIVATING_SUBMISSION,
  loadedSubmission,
  loadedSubmissions,
  LOADING_SUBMISSION,
  LOADING_SUBMISSIONS,
  savedSubmission,
  UPDATING_SUBMISSION,
  UPDATING_SUBMISSION_VALIDATION_FAIL,
} from "./actions";
import { authSelector } from "./selectors";
import { submissionService } from "../services";
import { UserRole } from "../services/domain/userRole";
import { History } from "history";
import { refreshActivityFeed } from "@ofqual/portal-activity-feed";

function* navigate(url: string) {
  const history: History = yield getContext("history");
  yield call(() => history.push(url));
}

function* watchSubmitSvrSaga() {
  yield takeEvery(CREATING_SUBMISSION, createSubmissionSaga);
}
function* watchSubmissionsSaga() {
  yield takeLatest(LOADING_SUBMISSIONS, submissionsSaga);
}
function* watchUserSaga() {
  yield takeLatest(LOADING_SUBMISSION, submissionSaga);
}

function* watchUpdateSubmissionSaga() {
  yield takeEvery(UPDATING_SUBMISSION, updateSubmissionSaga);
}
function* watchDeactivatingSubmissionSaga() {
  yield takeEvery(DEACTIVATING_SUBMISSION, deactivateSubmissionSaga);
}

function* createSubmissionSaga({ payload }: ActionPayload<SvrSubmission>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const rolesToCheckFor = [UserRole.ITAdmin, UserRole.OfqualSubmissionAdmin];
  const isRiskTeamUser = auth.roles.some((role: number) =>
    rolesToCheckFor.includes(role)
  );
  const validatedResponse: ValidatedResponse<SvrSubmission> = yield call(
    [submissionService, submissionService.createSubmission],
    payload,
    {
      token: auth.token,
    }
  );

  if (validatedResponse.isValid) {
    yield put({ type: CREATED_SUBMISSION });
    if (isRiskTeamUser) {
      yield call(() => navigate(`/svr/${validatedResponse.data}`));
    }
  } else {
    yield put({
      type: CREATING_SUBMISSION_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* submissionsSaga({
  payload,
}: ActionPayload<PagedResponse<SvrSubmission>>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const submissions: PagedResponse<SvrSubmission> = yield call(
    [submissionService, submissionService.search],
    { ...payload },
    { token: auth.token }
  );
  yield put(loadedSubmissions(submissions));
}

function* submissionSaga({ payload }: ActionPayload<string>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const user: SvrSubmission = yield call(
    [submissionService, submissionService.get],
    payload,
    {
      token: auth.token,
    }
  );
  yield put(loadedSubmission(user));
}

function* updateSubmissionSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);
  const { id, patchWithMeta } = payload as {
    id: string;
    patchWithMeta: PatchWithMeta<SvrSubmission>;
  };

  const validatedResponse: ValidatedResponse<SvrSubmission> = yield call(
    [submissionService, submissionService.updateSubmission],
    id,
    patchWithMeta,
    {
      token: auth.token,
    }
  );

  if (validatedResponse.isValid) {
    yield put(refreshActivityFeed(id));
    yield put(savedSubmission(validatedResponse.data));
  } else {
    yield put({
      type: UPDATING_SUBMISSION_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* deactivateSubmissionSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);
  const { id } = payload as {
    id: string;
  };

  const submission: SvrSubmission = yield call(
    [submissionService, submissionService.deactivateSubmission],
    id,
    {
      token: auth.token,
    }
  );

  yield put(savedSubmission(submission));
}

export function* rootSaga() {
  yield all([
    watchSubmitSvrSaga(),
    watchSubmissionsSaga(),
    watchUserSaga(),
    watchUpdateSubmissionSaga(),
    watchDeactivatingSubmissionSaga(),
  ]);
}
